import React, { useMemo, useRef, useState } from 'react';

import type { EChartsOption } from 'echarts';
import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';
import MUIProvider from '~/contexts/MUIProvider';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import AgentHubChart from './AgentHubChart';
import AgentHubControls from './AgentHubControls';
import AgentHubTable, { type Column } from './AgentHubTable';
import AgentHubTile from './AgentHubTile';
import { type ValueKind, compactText, valueText } from './format';
import {
  AGENT_HUB_PAGE_ID,
  AGENT_SOURCE,
  type AgentHubFilters,
  type AgentRow,
  type BreakdownRow,
  CADENCES,
  CHART_METRICS,
  type CardProblem,
  type CardResult,
  FINANCE_FIELDS,
  type FinanceRow,
  type MeasureKey,
  ORDER_SOURCE,
  SIGNUP_LEVELS,
  SPLITS,
  type SignupPoint,
  TABS,
  type Tab as TabValue,
  type TrendPoint,
  agentKpisOf,
  agentRowsOf,
  breakdownOf,
  comparisonLabel,
  csvOf,
  filtersError,
  financeRowsOf,
  isProblem,
  kpisOf,
  lensFilters,
  parseFilters,
  periodLabel,
  readCard,
  signupTrendOf,
  trendOf,
  writeFilters,
} from './model';
import { agentHubPageView, editFromBridge } from './pageAdapter';

const LENS_ID = nativeLensId(AGENT_HUB_PAGE_ID);

const MEASURE_KINDS: Record<MeasureKey, ValueKind> = {
  grossSales: 'money',
  margin: 'money',
  netMargin: 'money',
  commission: 'money',
  agentNett: 'money',
  override: 'money',
  packages: 'count',
  nights: 'count',
  orders: 'count',
  agents: 'count',
  agencies: 'count',
  aov: 'money',
};
const ORDER_TILES: ReadonlyArray<{ key: MeasureKey; label: string }> = [
  { key: 'agents', label: 'Agents w/ Orders' },
  { key: 'agencies', label: 'Agencies w/ Orders' },
  { key: 'orders', label: 'Orders' },
  { key: 'grossSales', label: 'Gross Sales AUD' },
  { key: 'margin', label: 'Margin AUD' },
  { key: 'netMargin', label: 'Net Margin AUD' },
  { key: 'commission', label: 'Agent Comms.' },
  { key: 'aov', label: 'Average Order Value' },
];
const BREAKDOWN_MEASURES: ReadonlyArray<{ key: MeasureKey; label: string }> = [
  { key: 'orders', label: 'Orders' },
  { key: 'grossSales', label: 'Gross Sales AUD' },
  { key: 'margin', label: 'Margin AUD' },
  { key: 'netMargin', label: 'Net Margin AUD' },
  { key: 'commission', label: 'Agent Commission' },
  { key: 'agentNett', label: 'Agent Nett AUD' },
  { key: 'override', label: 'Override AUD' },
  { key: 'aov', label: 'Average Order Value' },
  { key: 'packages', label: 'Packages' },
  { key: 'nights', label: 'Room Nights' },
];
const FINANCE_LABELS: Record<(typeof FINANCE_FIELDS)[number], string> = {
  transactionDate: 'Transaction date',
  affiliation: 'Agency affiliation',
  agency: 'Agency name',
  agentId: 'Agency employee ID',
  bookingNumber: 'Booking number',
  itemId: 'Item ID',
  orderId: 'Order ID',
  offerId: 'Offer ID',
  product: 'Product',
  category: 'Category',
  vendor: 'Vendor',
  offerName: 'Offer name',
  transactionType: 'Transaction type',
  costCurrency: 'Cost currency',
  orderStatus: 'Order status',
};

function download(name: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function csvFor<Row>(title: string, scope: string, columns: Array<Column<Row>>, rows: Array<Row>) {
  return csvOf([
    ['Report', `Agent Hub Dashboard · ${title}`],
    ['Scope', scope],
    columns.map((column) => column.label),
    ...rows.map((row) => columns.map((column) => column.value(row))),
  ]);
}

function barOption(points: Array<TrendPoint>, metric: MeasureKey, accent: string, muted: string): EChartsOption {
  const kind = MEASURE_KINDS[metric];
  const visible = 24;
  return {
    grid: { left: 8, right: 16, top: 24, bottom: 56, containLabel: true },
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value) => valueText(typeof value === 'number' ? value : null, kind),
    },
    xAxis: { type: 'category', data: points.map((point) => point.label) },
    yAxis: { type: 'value', axisLabel: { formatter: (value: number) => compactText(value, kind) } },
    dataZoom: [
      {
        type: 'slider',
        startValue: Math.max(0, points.length - visible),
        endValue: points.length - 1,
        height: 18,
        bottom: 8,
      },
    ],
    series: [
      {
        type: 'bar',
        name: CHART_METRICS.find((item) => item.value === metric)?.label,
        data: points.map((point) => ({
          value: point.measures[metric],
          itemStyle: { color: point.current ? accent : muted },
        })),
      },
    ],
  };
}

function signupOption(
  points: Array<SignupPoint>,
  level: AgentHubFilters['signupLevel'],
  accent: string,
): EChartsOption {
  return {
    grid: { left: 8, right: 16, top: 24, bottom: 56, containLabel: true },
    tooltip: { trigger: 'axis' },
    xAxis: { type: 'category', data: points.map((point) => point.label) },
    yAxis: { type: 'value' },
    dataZoom: [{ type: 'slider', height: 18, bottom: 8 }],
    series: [
      {
        type: 'line',
        name: `Cumulative ${SIGNUP_LEVELS.find((item) => item.value === level)?.label.toLowerCase()}`,
        areaStyle: { opacity: 0.12 },
        itemStyle: { color: accent },
        data: points.map((point) => point.cumulative[level]),
      },
    ],
  };
}

function AgentHubContent() {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const location = useLocation();
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = useState(false);
  const tableSearch = new URLSearchParams(location.search);
  const updateTableSearch = (key: string, value: string) => {
    const next = new URLSearchParams(location.search);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    navigate({ pathname: location.pathname, search: next.toString(), hash: location.hash }, { replace: true });
  };
  const filters = useMemo(() => parseFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (change: Partial<AgentHubFilters>) => {
    navigate({
      pathname: location.pathname,
      search: writeFilters({ ...filters, ...change }, new URLSearchParams(location.search)).toString(),
      hash: location.hash,
    });
  };
  const error = filtersError(filters);
  // Presentation-only parameters (tab, refunds, cadence, split…) leave the request, and so its query key, unchanged.
  const request = useMemo(() => lensFilters(filters), [filters]);
  const query = useGetReportingLeLensData(error ? undefined : LENS_ID, request);
  const cards = query.data?.cards ?? [];
  const lastOptions = useRef<Record<string, Array<string>>>({});
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const ready = !error && query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;
  const card = (id: string) => readCard(cards, id);
  const kpiCard = card('o_kpi');
  const range = isProblem(kpiCard) ? `${filters.from} to ${filters.to}` : periodLabel(kpiCard.card, filters.from);
  const prior = isProblem(kpiCard) ? null : comparisonLabel(kpiCard.card);
  const scope = `${range}${prior ? ` vs ${prior}` : ''} · ${filters.refunds === 'AR' ? 'After refunds (AR)' : 'Before refunds (BR)'}`;

  const kpis = kpisOf(kpiCard, card('o_kpi_counts'), filters.refunds);
  const daily = card('o_trend');
  const trend = isProblem(daily) ? [] : trendOf(daily, card(`o_trend_counts_${filters.cadence}`), filters);
  const splitSums = card(`o_split_${filters.split}`);
  const breakdown = isProblem(splitSums)
    ? []
    : breakdownOf(splitSums, card(`o_split_${filters.split}_orders`), filters.split, filters.depth, filters.refunds);
  const financeCard = card('f_items');
  const finance = isProblem(financeCard) ? [] : financeRowsOf(financeCard);
  const agentKpis = agentKpisOf(card('a_kpi_signups'), card('a_kpi_status'));
  const signupCard = card('a_signup_trend');
  const signups = isProblem(signupCard) ? [] : signupTrendOf(signupCard, filters.cadence);
  const agentsCard = card('a_agents');
  const agentRows = isProblem(agentsCard) ? [] : agentRowsOf(agentsCard, filters);

  const splitLabels =
    filters.split === 'agent_path'
      ? ['Affiliation', 'Agency', 'Agent'].slice(0, filters.depth)
      : [SPLITS.find((item) => item.value === filters.split)?.label ?? 'Split'];
  const breakdownColumns: Array<Column<BreakdownRow>> = [
    ...splitLabels.map((label, index) => ({
      key: `part${index}`,
      label,
      kind: 'text' as const,
      value: (row: BreakdownRow) => row.parts[index] ?? '',
    })),
    ...BREAKDOWN_MEASURES.map(({ key, label }) => ({
      key,
      label: [
        'grossSales',
        'margin',
        'netMargin',
        'commission',
        'agentNett',
        'override',
        'packages',
        'nights',
      ].includes(key)
        ? `${label} (${filters.refunds})`
        : label,
      kind: MEASURE_KINDS[key],
      value: (row: BreakdownRow) => row.measures[key],
    })),
  ];
  const trendColumns: Array<Column<TrendPoint>> = [
    {
      key: 'bucket',
      label: CADENCES.find((item) => item.value === filters.cadence)?.label ?? 'Date',
      kind: 'text',
      value: (row) => row.bucket,
    },
    ...ORDER_TILES.map(({ key, label }) => ({
      key,
      label,
      kind: MEASURE_KINDS[key],
      value: (row: TrendPoint) => row.measures[key],
    })),
  ];
  const financeColumns: Array<Column<FinanceRow>> = [
    ...FINANCE_FIELDS.map((field) => ({
      key: field,
      label: FINANCE_LABELS[field],
      kind: 'text' as const,
      value: (row: FinanceRow) => row[field],
    })),
    { key: 'grossSales', label: 'Gross sales AUD', kind: 'money', value: (row) => row.grossSales },
    { key: 'costSales', label: 'Cost of sales AUD', kind: 'money', value: (row) => row.costSales },
    { key: 'margin', label: 'Margin AUD', kind: 'money', value: (row) => row.margin },
  ];
  const signupColumns: Array<Column<SignupPoint>> = [
    {
      key: 'bucket',
      label: CADENCES.find((item) => item.value === filters.cadence)?.label ?? 'Date',
      kind: 'text',
      value: (row) => row.bucket,
    },
    ...SIGNUP_LEVELS.map(({ value, label }) => ({
      key: value,
      label: `${label} signed up`,
      kind: 'count' as const,
      value: (row: SignupPoint) => row.added[value],
    })),
    ...SIGNUP_LEVELS.map(({ value, label }) => ({
      key: `${value}Total`,
      label: `${label} to date`,
      kind: 'count' as const,
      value: (row: SignupPoint) => row.cumulative[value],
    })),
  ];
  const agentColumns: Array<Column<AgentRow>> = [
    ...(filters.showAffiliation
      ? [{ key: 'affiliation', label: 'Affiliation', kind: 'text' as const, value: (row: AgentRow) => row.affiliation }]
      : []),
    ...(filters.showAgencies
      ? [{ key: 'agency', label: 'Agency', kind: 'text' as const, value: (row: AgentRow) => row.agency }]
      : []),
    ...(filters.showAgents
      ? [{ key: 'agent', label: 'Agent', kind: 'text' as const, value: (row: AgentRow) => row.agent }]
      : []),
    { key: 'signupDate', label: 'Sign-up date', kind: 'text', value: (row) => row.signupDate },
    { key: 'firstOrderDate', label: 'First order', kind: 'text', value: (row) => row.firstOrderDate },
    { key: 'lastOrderDate', label: 'Last order', kind: 'text', value: (row) => row.lastOrderDate },
    { key: 'grossSales', label: 'Gross sales AUD', kind: 'money', value: (row) => row.grossSales },
    { key: 'margin', label: 'Margin AUD', kind: 'money', value: (row) => row.margin },
    { key: 'orders', label: 'Orders', kind: 'count', value: (row) => row.orders },
    { key: 'items', label: 'Items', kind: 'count', value: (row) => row.items },
    { key: 'commission', label: 'Total commission AUD', kind: 'money', value: (row) => row.commission },
  ];

  const summary: Record<string, number | null> | null =
    filters.tab === 'agents'
      ? agentKpis && {
          affiliateSignups: agentKpis.signups.affiliates,
          agencySignups: agentKpis.signups.agencies,
          agentSignups: agentKpis.signups.agents,
          active: agentKpis.active[filters.signupLevel],
          churned: agentKpis.churned[filters.signupLevel],
        }
      : (kpis?.current ?? null);
  const asSection = <Row,>(
    title: string,
    rows: Array<Row>,
    columns: Array<Column<Row>>,
    result: CardResult | CardProblem,
  ) => ({
    title,
    rows: rows.map((row) => ({
      label: String(columns[0].value(row) ?? ''),
      values: Object.fromEntries(columns.slice(1).map((column) => [column.label, column.value(row)])),
    })),
    truncated: !isProblem(result) && result.truncated,
  });
  const pageView = agentHubPageView({
    ready,
    filters,
    options: lastOptions.current,
    period: range,
    comparison: prior,
    summary,
    sections:
      filters.tab === 'orders'
        ? [asSection('Agency Breakdown', breakdown, breakdownColumns, splitSums)]
        : filters.tab === 'agents'
          ? [asSection('Affiliations, agencies and agents', agentRows, agentColumns, agentsCard)]
          : [asSection('Finance Report', finance, financeColumns, financeCard)],
  });
  usePageBridge({
    pageId: AGENT_HUB_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(editFromBridge(changes, pageView, filters));
    },
  });

  const problem = (result: CardResult | CardProblem) =>
    isProblem(result) ? (
      <Alert severity={query.isFetching ? 'info' : 'error'}>{query.isFetching ? 'Loading…' : result.problem}</Alert>
    ) : result.truncated ? (
      <Alert severity="warning">
        This section reached its row limit, so some rows are not shown. Narrow the filters or dates.
      </Alert>
    ) : null;
  const grid = (columns: Record<string, string>, children: React.ReactNode) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: columns, gap: 1.5 }}>{children}</Box>
  );
  const cadenceLabel = CADENCES.find((item) => item.value === filters.cadence)?.label ?? '';

  const orders = (
    <Stack gap={2}>
      {problem(kpiCard)}
      {kpis &&
        grid(
          { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))', xl: 'repeat(8, minmax(0, 1fr))' },
          ORDER_TILES.map(({ key, label }) => (
            <AgentHubTile
              key={key}
              label={
                ['grossSales', 'margin', 'netMargin', 'commission'].includes(key)
                  ? `${label} (${filters.refunds})`
                  : label
              }
              value={kpis.current[key]}
              previous={kpis.previous?.[key] ?? null}
              kind={MEASURE_KINDS[key]}
            />
          )),
        )}
      {problem(daily)}
      {!isProblem(daily) && (
        <AgentHubChart
          title={`${CHART_METRICS.find((item) => item.value === filters.chartMetric)?.label} by ${cadenceLabel.toLowerCase()} period`}
          option={barOption(trend, filters.chartMetric, tokens.accent, theme.palette.action.disabled)}
          caption={`${ORDER_SOURCE} · last 60 months to yesterday · highlighted bars overlap ${range}`}
        />
      )}
      {!isProblem(daily) && (
        <AgentHubTable
          title={`Measures by ${cadenceLabel.toLowerCase()} period`}
          caption={`${ORDER_SOURCE} · ${filters.refunds === 'AR' ? 'after refunds' : 'before refunds'} · distinct counts per period`}
          rows={trend}
          columns={trendColumns}
          rowKey={(row) => row.bucket}
          defaultSort="bucket"
          onCsv={(rows) =>
            download(`agent-hub-trend-${filters.cadence}.csv`, csvFor('Trend', scope, trendColumns, rows))
          }
        />
      )}
      {problem(splitSums)}
      {!isProblem(splitSums) && (
        <AgentHubTable
          title="Agency Breakdown"
          caption={`${ORDER_SOURCE} · ${scope}`}
          rows={breakdown}
          columns={breakdownColumns}
          rowKey={(row) => row.key}
          defaultSort="grossSales"
          onCsv={(rows) =>
            download(
              `agent-hub-breakdown-${filters.from}-${filters.to}.csv`,
              csvFor('Agency Breakdown', scope, breakdownColumns, rows),
            )
          }
        />
      )}
    </Stack>
  );

  const levelLabel = SIGNUP_LEVELS.find((item) => item.value === filters.signupLevel)?.label ?? '';
  const agents = (
    <Stack gap={2}>
      {problem(card('a_kpi_signups'))}
      {agentKpis &&
        grid(
          { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
          <>
            <AgentHubTile
              label="Affiliate sign-ups"
              value={agentKpis.signups.affiliates}
              previous={agentKpis.previousSignups?.affiliates ?? null}
              kind="count"
            />
            <AgentHubTile
              label="Agency sign-ups"
              value={agentKpis.signups.agencies}
              previous={agentKpis.previousSignups?.agencies ?? null}
              kind="count"
            />
            <AgentHubTile
              label="Agent sign-ups"
              value={agentKpis.signups.agents}
              previous={agentKpis.previousSignups?.agents ?? null}
              kind="count"
            />
            <AgentHubTile
              label={`Active ${levelLabel.toLowerCase()}`}
              value={agentKpis.active[filters.signupLevel]}
              kind="count"
              note="Ordered in the last 90 days"
            />
            <AgentHubTile
              label={`Churned ${levelLabel.toLowerCase()}`}
              value={agentKpis.churned[filters.signupLevel]}
              kind="count"
              note="No orders, 180+ days since sign-up"
            />
          </>,
        )}
      {problem(signupCard)}
      {!isProblem(signupCard) && (
        <AgentHubChart
          title={`Cumulative ${levelLabel.toLowerCase()} sign-ups`}
          option={signupOption(signups, filters.signupLevel, tokens.accent)}
          caption={`${AGENT_SOURCE} · all sign-ups to yesterday by ${cadenceLabel.toLowerCase()} period`}
        />
      )}
      {!isProblem(signupCard) && (
        <AgentHubTable
          title={`Sign-ups by ${cadenceLabel.toLowerCase()} period`}
          caption={`${AGENT_SOURCE} · agencies and affiliates count on their first sign-up`}
          rows={signups}
          columns={signupColumns}
          rowKey={(row) => row.bucket}
          defaultSort="bucket"
          onCsv={(rows) =>
            download(
              `agent-hub-signups-${filters.cadence}.csv`,
              csvFor('Sign-ups', 'All sign-ups', signupColumns, rows),
            )
          }
        />
      )}
      {problem(agentsCard)}
      {!isProblem(agentsCard) && (
        <AgentHubTable
          title="Affiliations, agencies and agents"
          caption={`${AGENT_SOURCE} · all-time sales per agent · agents show their ID where the name is masked`}
          rows={agentRows}
          columns={agentColumns}
          rowKey={(row) => row.key}
          defaultSort="grossSales"
          searchable
          search={tableSearch.get('ahAgentsSearch') ?? ''}
          onSearchChange={(search) => updateTableSearch('ahAgentsSearch', search)}
          onCsv={(rows) =>
            download(
              'agent-hub-agents.csv',
              csvFor('Affiliations, agencies and agents', 'All agents', agentColumns, rows),
            )
          }
        />
      )}
    </Stack>
  );

  const financeView = (
    <Stack gap={2}>
      {problem(financeCard)}
      {!isProblem(financeCard) && (
        <AgentHubTable
          title="Finance Report"
          caption={`${ORDER_SOURCE} · sale items in ${range}, excluding zero-value merchant fees`}
          rows={finance}
          columns={financeColumns}
          rowKey={(row) => `${row.itemId}${row.transactionDate}${row.transactionType}`}
          defaultSort="transactionDate"
          searchable
          search={tableSearch.get('ahFinanceSearch') ?? ''}
          onSearchChange={(search) => updateTableSearch('ahFinanceSearch', search)}
          onCsv={(rows) =>
            download(
              `agent-hub-finance-${filters.from}-${filters.to}.csv`,
              csvFor('Finance Report', range, financeColumns, rows),
            )
          }
        />
      )}
    </Stack>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={1}
        >
          <Box>
            <Typography component="h1" variant="h4" sx={{ fontSize: { xs: 25, sm: 30 }, color: tokens.accent }}>
              Agent Hub Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filters.tab === 'agents' ? 'Sign-up dates' : 'Sydney transaction dates'} · {scope}
            </Typography>
          </Box>
          <Stack direction="row" gap={1} alignItems="center">
            <Button size="small" onClick={() => setInfoOpen(true)}>
              How to / info
            </Button>
            <PageChatButton />
          </Stack>
        </Stack>
        <Tabs
          value={filters.tab}
          onChange={(_, tab: TabValue) => update({ tab })}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Agent Hub views"
        >
          {TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={tab.label} />
          ))}
        </Tabs>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" variant="outlined" color="primary" label="Live" />
          <Typography variant="caption" color="text.secondary">
            {filters.tab === 'agents' ? AGENT_SOURCE : ORDER_SOURCE} · rebuilt daily ·{' '}
            {query.isFetching ? 'updating…' : range}
          </Typography>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {failure && (
          <Alert
            severity={failure.status === 404 || failure.status === 403 ? 'warning' : 'error'}
            action={
              <Button color="inherit" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          >
            {failure.status === 404
              ? 'Agent Hub Dashboard is not registered in Library for this environment yet. A reporting admin registers it under Playground → Lens pages → Register native pages.'
              : failure.status === 403
                ? 'You do not have access to Agent Hub Dashboard. Ask a reporting admin to add you to its Library folder.'
                : failure.message || 'Agent Hub Dashboard could not load.'}
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 300px' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {!error && !failure && query.isPending && (
              <Stack gap={2}>
                <Skeleton variant="rounded" height={90} />
                <Skeleton variant="rounded" height={320} />
              </Stack>
            )}
            {!error &&
              query.data &&
              (filters.tab === 'orders' ? orders : filters.tab === 'agents' ? agents : financeView)}
          </Box>
          <AgentHubControls filters={filters} options={lastOptions.current} onChange={update} />
        </Box>
      </Stack>
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>How to read Agent Hub</DialogTitle>
        <DialogContent>
          <Stack gap={1.5}>
            <Typography variant="body2">
              Orders and Finance read {ORDER_SOURCE} for the LE Agent Hub and CVFR Holidays brands, with a known
              product, category, agency country and order currency. After refunds (AR) nets refunds processed in the
              window, whatever day the sale was made.
            </Typography>
            <Typography variant="body2">
              Net margin is margin less agent commission. Agent nett is gross sales less commission, and the override is
              2% of agent nett from each affiliation’s start date. Average order value is gross sales before refunds
              divided by orders. Orders, agents and agencies count distinct values on sale rows.
            </Typography>
            <Typography variant="body2">
              Agents reads {AGENT_SOURCE}; agents without an affiliation show under Unknown. An agency or affiliate
              counts as signed up on its first agent’s sign-up date. Active means an order in the last 90 days; churned
              means signed up over 180 days ago and never ordered. Both are as of today.
            </Typography>
            <Typography variant="body2">
              KPI changes compare the window with the same number of days just before it. Agent names are masked in the
              warehouse, so agents show their ID where the name is hidden.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default function AgentHubPage() {
  return (
    <MUIProvider>
      <PageChatLayout>
        <AgentHubContent />
      </PageChatLayout>
    </MUIProvider>
  );
}
